import { useMemo } from "react";
import { NavLink, type RouteObject } from "react-router";
import { useNavigate } from "react-router";

import type { FormValues } from "@prequel/import-react";
import { Button, ButtonStyle, checkTabIsActive, PageHeading, Tabs } from "@prequel/shared-components";

import DestinationDetails from "./DestinationDetails";
import DestinationForm from "./DestinationForm";
import DestinationPage, { DestinationDetailsPage, useDestinationContext } from "./DestinationPage";
import DestinationsTable from "./DestinationsTable";
import ProductDetails from "./ProductDetails";
import ProductPage, { ProductDetailsPage } from "./ProductPage";
import ProductsTable from "./ProductsTable";

import Breadcrumbs from "@/components/Breadcrumbs";

const tabs = [
  <NavLink key="Destinations" to="/import/destinations" className={checkTabIsActive} replace end>
    Destinations
  </NavLink>,
  <NavLink key="Products" to="/import/destinations/products" className={checkTabIsActive} replace end>
    Products
  </NavLink>,
];

const DestinationsListLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4 pb-16">
        <PageHeading title="Destinations & Products" breadcrumbs={<Breadcrumbs />}>
          <Button
            text="Add Destination"
            onClick={() => navigate("/import/destinations/new")}
            type={ButtonStyle.SECONDARY}
          />
        </PageHeading>
        <Tabs tabs={tabs} />
        <DestinationsTable />
      </div>
    </div>
  );
};

const ProductsListLayout = () => {
  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4 pb-16">
        <PageHeading title="Destinations & Products" breadcrumbs={<Breadcrumbs />} />
        <Tabs tabs={tabs} />
        <ProductsTable />
      </div>
    </div>
  );
};

const DestinationFormNewLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-3xl px-7 py-4">
        <PageHeading
          title="Add Destination"
          onGoBack={() => navigate("/import/destinations")}
          breadcrumbs={<Breadcrumbs />}
        />
        <DestinationForm />
      </div>
    </div>
  );
};

const DestinationFormEditLayout = () => {
  const navigate = useNavigate();
  const { destination } = useDestinationContext();
  const initialValues = useMemo(() => {
    const values = { ...destination } as FormValues;
    delete values.id;
    delete values.created_at;
    delete values.updated_at;
    return values;
  }, [destination]);

  return (
    <div className="h-full flex-col">
      <div className="max-w-3xl px-7 py-4">
        <PageHeading
          title="Edit Destination"
          onGoBack={() => navigate(`/import/destinations/${destination.id}`)}
          breadcrumbs={<Breadcrumbs dynamicLabel={destination.name} />}
        />
        <DestinationForm
          destinationId={destination.id}
          initialValues={initialValues}
          cancelPath={`/import/destinations/${destination.id}`}
        />
      </div>
    </div>
  );
};

export const destinationsRouteChildren: RouteObject = {
  path: "destinations",
  handle: {
    breadcrumb: () => ({ label: "Destinations & Products" }),
  },
  children: [
    {
      index: true,
      element: <DestinationsListLayout />,
    },
    {
      path: "new",
      handle: {
        breadcrumb: () => ({ label: "Add Destination" }),
      },
      element: <DestinationFormNewLayout />,
    },
    {
      path: "products",
      element: <ProductsListLayout />,
    },
    {
      path: "products/:productID",
      handle: {
        breadcrumb: () => ({
          label: "Product Details",
          dynamic: true,
        }),
      },
      element: <ProductPage />,
      children: [
        {
          element: <ProductDetailsPage />,
          children: [
            {
              index: true,
              element: <ProductDetails />,
            },
          ],
        },
      ],
    },
    {
      path: ":destinationID",
      handle: {
        breadcrumb: () => ({
          label: "Destination Details",
          dynamic: true,
        }),
      },
      element: <DestinationPage />,
      children: [
        {
          path: "edit",
          handle: {
            breadcrumb: () => ({ label: "Edit Destination" }),
          },
          element: <DestinationFormEditLayout />,
        },
        {
          element: <DestinationDetailsPage />,
          children: [
            {
              index: true,
              element: <DestinationDetails />,
            },
          ],
        },
      ],
    },
  ],
};
